import { create } from "zustand";
import { createJSONStorage, persist, type StateStorage } from "zustand/middleware";

const authStorageKey = "medicare-home-auth";
const rememberPreferenceKey = "carevinco:remember-me";

const browserAuthStorage: StateStorage = {
  getItem: (name) => {
    if (typeof window === "undefined") return null;
    return window.sessionStorage.getItem(name) || window.localStorage.getItem(name);
  },
  removeItem: (name) => {
    if (typeof window === "undefined") return;
    window.sessionStorage.removeItem(name);
    window.localStorage.removeItem(name);
  },
  setItem: (name, value) => {
    if (typeof window === "undefined") return;
    const shouldRemember = window.localStorage.getItem(rememberPreferenceKey) === "true";
    const target = shouldRemember ? window.localStorage : window.sessionStorage;
    const staleTarget = shouldRemember ? window.sessionStorage : window.localStorage;
    staleTarget.removeItem(name);
    target.setItem(name, value);
  },
};

export function setRememberMePreference(remember: boolean) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(rememberPreferenceKey, String(remember));
  if (!remember) window.localStorage.removeItem(authStorageKey);
}

export function getRememberMePreference() {
  return typeof window !== "undefined" && window.localStorage.getItem(rememberPreferenceKey) === "true";
}

export type AppRole = "user" | "provider" | "admin" | "super_admin";

export type AuthPayload = {
  accessToken: string;
  email?: string | null;
  firebaseUid: string;
  gender?: string | null;
  name?: string | null;
  profileImageUrl?: string | null;
  refreshToken?: string;
  role: AppRole;
  userId?: string | null;
};

type AuthState = {
  accessToken?: string;
  email?: string | null;
  firebaseUid?: string;
  gender?: string | null;
  hasHydrated: boolean;
  isAuthenticated: boolean;
  isAuthRouting: boolean;
  isLogoutPending: boolean;
  name?: string | null;
  profileImageUrl?: string | null;
  refreshToken?: string;
  role?: AppRole;
  userId?: string | null;
  clearAuth: () => void;
  markHydrated: () => void;
  setAuth: (payload: AuthPayload) => void;
  setAuthRouting: (isRouting: boolean) => void;
  setLogoutPending: (isPending: boolean) => void;
};

const baseAuthState = {
  accessToken: "",
  email: undefined,
  firebaseUid: undefined,
  gender: undefined,
  name: undefined,
  profileImageUrl: undefined,
  refreshToken: undefined,
  role: undefined,
  userId: undefined,
};

const initialAuthState = {
  ...baseAuthState,
  hasHydrated: false,
  isAuthenticated: false,
  isAuthRouting: false,
  isLogoutPending: false,
};

const noopStorage = {
  getItem: () => null,
  removeItem: () => undefined,
  setItem: () => undefined,
};

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      ...initialAuthState,
      clearAuth: () =>
        set((state) => ({
          ...baseAuthState,
          hasHydrated: state.hasHydrated,
          isAuthenticated: false,
          isAuthRouting: false,
          isLogoutPending: false,
        })),
      markHydrated: () => set({ hasHydrated: true }),
      setAuth: (payload) =>
        set((state) => ({
          ...state,
          ...payload,
          email: payload.email ?? null,
          gender: payload.gender ?? null,
          isAuthenticated: true,
          isLogoutPending: false,
          name: payload.name ?? null,
          profileImageUrl: payload.profileImageUrl ?? null,
          refreshToken: payload.refreshToken,
          userId: payload.userId ?? null,
        })),
      setAuthRouting: (isRouting) => set({ isAuthRouting: isRouting }),
      setLogoutPending: (isPending) => set({ isLogoutPending: isPending }),
    }),
    {
      name: authStorageKey,
      onRehydrateStorage: () => (state) => {
        state?.markHydrated();
      },
      partialize: (state) => ({
        accessToken: state.accessToken,
        email: state.email,
        firebaseUid: state.firebaseUid,
        gender: state.gender,
        isAuthenticated: state.isAuthenticated,
        name: state.name,
        profileImageUrl: state.profileImageUrl,
        refreshToken: state.refreshToken,
        role: state.role,
        userId: state.userId,
      }),
      storage: createJSONStorage(() => (typeof window === "undefined" ? noopStorage : browserAuthStorage)),
    },
  ),
);
